import {
    IGuestDetails,
    IGuestListItem,
    InfiniteList,
    ISort,
    ISortOption,
    ISpeedDialActionProps,
    SearchField,
    SortField,
    SortOrder,
    SpeedDial,
    StringUtils,
    useConfirmContext,
    useUrlParams,
} from '@localina/core';
import {
    DownloadIcon,
    FileUploadIcon,
    ImportExportHorizontalIcon,
    ImportExportVerticalIcon,
    UploadIcon,
} from '@localina/icons';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { useGetAuthUser } from '../../api/queries/auth';
import { useGuestList, useGuestsDownload } from '../../api/queries/guests';
import { queryKeys } from '../../api/queries/query-keys';
import { useRestaurant } from '../../api/queries/restaurants';
import { GuestListItem, GuestsImportView, Page } from '../../components';
import ImportMetadataProgress from '../../components/ImportMetadataProgress/ImportMetadataProgress';
import ReservationsImportView from '../../components/ReservationImportView/ReservationsImportView';
import { GuestsExportFormat, Path, SortBy } from '../../enums';
import { PathUtils, RoleUtils } from '../../utils';
import { GuestsDatabaseEmpty, GuestsNoResults } from './GuestsEmptyStates';

interface IPathParams {
    restaurantId: string;
}

const Guests: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const listContainerRef = useRef<HTMLDivElement>(null);

    const { restaurantId } = useParams() as unknown as IPathParams;
    const [importInitiated, setImportInitiated] = useState(false);

    const [urlParams, setUrlParams] = useUrlParams();

    const [queryParam, sortByParam, sortOrderParam] = [
        urlParams.get('query') || '',
        urlParams.get('sortBy') || '',
        urlParams.get('sortOrder') || '',
    ];
    const [query, setQuery] = React.useState<string>(queryParam);
    const authUserQuery = useGetAuthUser();

    const [guestsImportRequested, setGuestsImportRequested] = React.useState(false);
    const [reservationsImportViewOpen, setReservationsImportViewOpen] = React.useState(false);

    const restaurantQuery = useRestaurant();

    const guestsDownloadMutation = useGuestsDownload();

    const isSupportUser =
        authUserQuery.isSuccess && authUserQuery.data.user && RoleUtils.hasSupportRole(authUserQuery.data.user);

    const [filters, setFilters] = useState<{ query: string; sort: ISort }>({
        query: queryParam,
        sort: {
            by:
                sortByParam && Object.values(SortBy).includes(sortByParam as SortBy)
                    ? sortByParam
                    : SortBy.LAST_NAME_FIRST_NAME,
            order:
                sortOrderParam && Object.values(SortOrder).includes(sortOrderParam as SortOrder)
                    ? (sortOrderParam as SortOrder)
                    : SortOrder.ASC,
        },
    });

    const queryClient = useQueryClient();
    const guestListQuery = useGuestList(filters);

    const selectedGuestId = useMatch(Path.RESTAURANT_GUEST)?.params.guestId;

    const notDebouncedFilter = useMemo(
        () => ({
            ...filters,
            query,
        }),
        [filters, query],
    );

    const handleSearch = (value: string) => {
        setFilters((prevState) => ({ ...prevState, query: value }));
    };

    const handleClearSearch = () => {
        setQuery('');
        handleSearch('');
    };

    const handleSort = (sort: ISort) => {
        setFilters((prevState) => ({ ...prevState, sort }));
    };

    const sortOptions: ISortOption[] = [
        { by: SortBy.LAST_NAME_FIRST_NAME, scale: 'alpha' as const },
        { by: SortBy.FIRST_NAME_LAST_NAME, scale: 'alpha' as const },
        { by: SortBy.RESERVATION_NUMBER, scale: 'count' as const },
    ].flatMap(({ by, scale }) =>
        (scale === 'alpha' ? [SortOrder.ASC, SortOrder.DESC] : [SortOrder.DESC, SortOrder.ASC]).map((order) => ({
            by,
            order,
            label: t('guests.filter.sort.option', {
                label: t(`guests.filter.sort.options.${by}`),
                order: t(`guests.filter.sort.order.${scale}.${order}`),
            }),
        })),
    );

    const handleGuest = (item: IGuestListItem) => {
        queryClient.setQueryData(
            queryKeys.restaurants.single.guests.single(restaurantId, item.guest.id),
            (oldData?: IGuestDetails) =>
                oldData
                    ? undefined
                    : {
                          firstReservationDateTime: '',
                          lastReservationDateTime: '',
                          ...item,
                      },
        );

        navigate(
            PathUtils.generateUrl(Path.RESTAURANT_GUEST, {
                restaurantId,
                guestId: item.guest.id,
            }) + location.search,
            { replace: Boolean(selectedGuestId) },
        );
    };

    const handleImport = () => {
        setGuestsImportRequested(true);
    };

    const openReservationsImportView = () => {
        setReservationsImportViewOpen(true);
    };
    const handleReservationsUploadClose = () => {
        setReservationsImportViewOpen(false);
    };
    const triggerOpenImportDialog = () => {
        setImportInitiated(true);
    };

    const { snackbar } = useConfirmContext();

    const downloadGuests = () => {
        guestsDownloadMutation.mutate(
            {
                format: GuestsExportFormat.XLSX,
                fileName: StringUtils.combineStrings(
                    ['guests', restaurantQuery.data?.info.name, restaurantQuery.data?.id],
                    '_',
                ),
            },
            {
                onSuccess: () => {
                    snackbar({
                        severity: 'success',
                        msg: t('common.infoMessages.downloadStarted'),
                    });
                },
                onError: () => {
                    snackbar({
                        severity: 'error',
                        msg: t('common.infoMessages.downloadError'),
                    });
                },
            },
        );
    };

    const actions: ISpeedDialActionProps[] = [
        {
            label: t('guests.actions.export'),
            icon: <DownloadIcon />,
            onClick: downloadGuests,
        },
    ];

    actions.push({
        label: t('guests.actions.import'),
        icon: <FileUploadIcon />,
        onClick: handleImport,
    });

    if (isSupportUser) {
        actions.push({
            label: t('guests.actions.reservationsImport'),
            icon: <UploadIcon />,
            onClick: openReservationsImportView,
        });
    }

    const handleGuestUploadClose = () => {
        setGuestsImportRequested(false);
    };

    useEffect(() => {
        setUrlParams({
            query: notDebouncedFilter.query,
            sortBy: notDebouncedFilter.sort.by,
            sortOrder: notDebouncedFilter.sort.order,
        });
    }, [notDebouncedFilter]);

    useEffect(() => {
        if (!importInitiated) {
            handleReservationsUploadClose();
            handleGuestUploadClose();
        }
    }, [importInitiated]);

    const isLoading = guestsDownloadMutation.isLoading;

    const sortOrFilterChangesLoading =
        guestListQuery.isFetching && !guestListQuery.isFetchingNextPage && !guestListQuery.isFetchingPreviousPage;

    const guestItems = (guestListQuery.data?.pages || []).flatMap((page) =>
        page.items.map((item) => (
            <GuestListItem
                key={item.guest.id}
                guest={item}
                sort={page.sort || filters.sort}
                selected={item.guest.id === selectedGuestId}
                onClick={handleGuest}
            />
        )),
    );

    // keepPreviousData leaves the last result on screen while a new search loads, so the empty
    // state has to name the search that result belongs to, not the one being typed.
    const shownQueryRef = useRef(filters.query);
    if (guestListQuery.isSuccess && !guestListQuery.isPreviousData) {
        shownQueryRef.current = filters.query;
    }
    const shownQuery = shownQueryRef.current.trim();
    const isListEmpty = guestListQuery.isSuccess && guestItems.length === 0;
    const isDatabaseEmpty = isListEmpty && !shownQuery;
    const hasNoResults = isListEmpty && Boolean(shownQuery);

    const subtitle =
        (isDatabaseEmpty && t('guests.empty.subtitle')) ||
        (hasNoResults && t('guests.noResults.subtitle')) ||
        undefined;

    return (
        <Page name="guests" title={{ value: t('guests.title'), subtitle }} isLoading={isLoading}>
            {guestsImportRequested && (
                <GuestsImportView
                    restaurantId={restaurantId}
                    onClose={handleGuestUploadClose}
                    onStart={triggerOpenImportDialog}
                />
            )}
            {isSupportUser && reservationsImportViewOpen && (
                <ReservationsImportView
                    restaurantId={restaurantId}
                    onClose={handleReservationsUploadClose}
                    onStart={triggerOpenImportDialog}
                />
            )}
            {}
            <div className="guests-layout">
                <div id="guests">
                    <div className={StringUtils.combineStrings(['guests-filters', isDatabaseEmpty && 'is-inactive'])}>
                        <SearchField
                            label={t('guests.filter.search')}
                            value={query}
                            onChange={setQuery}
                            onSearch={handleSearch}
                            disabled={isDatabaseEmpty}
                        />
                        <SortField
                            label={t('guests.filter.sort.title')}
                            options={sortOptions}
                            sort={filters.sort}
                            onChange={handleSort}
                            disabled={isDatabaseEmpty}
                        />
                    </div>
                    {}
                    <div className="guests-list">
                        {sortOrFilterChangesLoading && <div className="guests-list__loading" role="progressbar" />}
                        {isDatabaseEmpty && <GuestsDatabaseEmpty />}
                        {hasNoResults && <GuestsNoResults query={shownQuery} onClear={handleClearSearch} />}
                        {!isListEmpty && (
                            <InfiniteList
                                ref={listContainerRef}
                                hasMore={Boolean(guestListQuery.hasNextPage)}
                                items={guestItems}
                                loadNext={guestListQuery.fetchNextPage}
                            />
                        )}
                    </div>
                    <SpeedDial
                        icon={<ImportExportVerticalIcon />}
                        openIcon={<ImportExportHorizontalIcon />}
                        label="import-export"
                        actions={actions}
                        fixed
                    />
                    <ImportMetadataProgress
                        restaurantId={restaurantId}
                        importInitiated={importInitiated}
                        setImportInitiated={setImportInitiated}
                    />
                </div>
                {}
                <Outlet />
            </div>
        </Page>
    );
};
export default Guests;
